import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { StatusCodesService } from 'src/app/features/service/status-codes.service';
import { ApplyBulkPodPayload } from 'src/app/features/model/hawb.model';
import { CustomErrorTooltipComponent } from 'src/app/shared/components/custom-error-tooltip/custom-error-tooltip.component';
import { DateTimePickerComponent } from 'src/app/shared/components/date-time-picker/date-time-picker.component';
import { SvgIconComponent } from 'src/app/shared/components/svg-icon/svg-icon.component';
import { MaterialModule } from 'src/app/shared/material.module';
import { IDropdownResponse } from 'src/app/shared/models/dynamic.model';
import { CommonService } from 'src/app/shared/services/common.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { SnakbarService } from 'src/app/shared/services/snakbar.service';
import {
  ActionIcons,
  MasterLookup,
  ModalPopupMessages,
  popupButtons,
  STORAGE_KEYS,
  StatusCodes,
  SvgIconFrom,
} from 'src/app/shared/utility/constants';
import {
  checkPodControlHasError,
  getPodFieldErrorMessage,
  isPodFormReady,
  podCarrierNameValidators,
  podDriverNameValidators,
  podCarrierRequiredValidator,
  podOptionalDateTimeValidator,
  POD_NAME_MAX_LENGTH,
  resolvePodDateTimeForApi,
  STATUS_CODE_POD_NAME_MAX_LENGTH,
} from 'src/app/shared/utility/pod-details.util';

export type BulkPodPopupMode = 'bulk' | 'details';

export interface BulkPodRowData {
  driverName?: string;
  carrierName?: string;
  dateTime?: string;
  timeZoneId?: number;
  pageMode?: string;
  readOnly?: boolean;
  /** Manifest POD (SPEC-BPM-001): locked time zone, Confirm label, defaults to now. */
  isManifest?: boolean;
  /** Saved Manifest POD row in edit: only the POD date/time can change (FR-BPM-UI-018). */
  podTimeOnly?: boolean;
}

export interface BulkPodDialogData {
  mode?: BulkPodPopupMode;
  hawbIds?: number[];
  rowData?: BulkPodRowData;
}

export interface BulkPodDetailsResult {
  driverName: string;
  carrierName: string;
  dateTimeAndTimezone: {
    dateTime: string;
    timeZoneId: number;
  } | null;
}

@Component({
  selector: 'app-bulk-pod-popup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    SvgIconComponent,
    DateTimePickerComponent,
    CustomErrorTooltipComponent,
  ],
  templateUrl: './bulk-pod-popup.component.html',
  styleUrl: './bulk-pod-popup.component.scss',
})
export class BulkPodPopupComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<BulkPodPopupComponent>);
  private readonly dialogData = inject(MAT_DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  public readonly commonService = inject(CommonService);
  private readonly statusCodesService = inject(StatusCodesService);
  private readonly snackbarService = inject(SnakbarService);
  private readonly localStorageService = inject(LocalStorageService);

  public readonly popupButtons = popupButtons;
  public readonly actionIcons = ActionIcons;
  public readonly svgIconFromList = SvgIconFrom;
  public dialogTitle = ModalPopupMessages.bulkPodHeader;
  public form!: FormGroup;
  public timeZones: IDropdownResponse[] = [];
  public isSubmitting = false;
  public readOnly = false;
  public isManifest = false;
  public podTimeOnly = false;
  public defaultTimeZoneId = 0;
  public setTooltipInfoWidth: Record<string, any> = {};
  public setInfoTooltipEvent: Record<string, MouseEvent> = {};
  public setInfoErrorTooltip: Record<string, boolean> = {};
  public errorMessages: Record<string, string> = {
    invalidDateTime: ModalPopupMessages.podDateTimeInvalid,
    carrierName: ModalPopupMessages.podCarrierNameRequiredField,
  };

  private mode: BulkPodPopupMode = 'bulk';
  private hawbIds: number[] = [];
  private rowData?: BulkPodRowData;
  private pendingDateTimePatch: {
    dateTime: string;
    timeZoneId: number;
  } | null = null;

  public get isDetailsMode(): boolean {
    return this.mode === 'details';
  }

  public get canSave(): boolean {
    return !this.readOnly && isPodFormReady(this.form);
  }

  /** AC1 names Confirm for the Manifest popup; Shipment popups keep Save (§11 Q9). */
  public get saveButtonLabel(): string {
    return this.isManifest ? popupButtons.Confirm : popupButtons.Save;
  }

  /** Status Codes tab details allow 126; the Bulk POD and Manifest POD APIs cap at 50. */
  public get nameMaxLength(): number {
    return this.isDetailsMode && !this.isManifest
      ? STATUS_CODE_POD_NAME_MAX_LENGTH
      : POD_NAME_MAX_LENGTH;
  }

  public get dateTimeLabel(): string {
    return this.isManifest ? 'Date and Time' : 'POD Time';
  }

  ngOnInit(): void {
    const payload: BulkPodDialogData =
      this.dialogData?.code ?? this.dialogData ?? {};
    this.mode = payload.mode ?? (payload.hawbIds?.length ? 'bulk' : 'details');
    this.hawbIds = [...new Set(payload.hawbIds ?? [])];
    this.rowData = payload.rowData;
    this.readOnly = !!payload.rowData?.readOnly;
    this.isManifest = !!payload.rowData?.isManifest;
    this.podTimeOnly = !this.readOnly && !!payload.rowData?.podTimeOnly;
    this.defaultTimeZoneId = this.getStoredTimeZoneId();
    this.dialogTitle =
      this.dialogData?.title ??
      (this.isDetailsMode
        ? ModalPopupMessages.additionalDetailsHeader
        : ModalPopupMessages.bulkPodHeader);
    this.buildForm();
    this.patchDetailsForm();
    this.loadTimeZones();
  }

  public onCancel(): void {
    this.dialogRef.close(false);
  }

  public onSave(): void {
    if (this.readOnly) {
      this.onCancel();
      return;
    }

    this.form.markAllAsTouched();
    if (!this.canSave || this.isSubmitting) {
      return;
    }

    if (this.isDetailsMode) {
      this.dialogRef.close(this.buildDetailsResult());
      return;
    }

    if (!this.hawbIds.length) {
      return;
    }

    const dateTimeValue = this.form.get('dateTimeAndTimezone')?.value;
    const { dateTime, timeZoneId } = resolvePodDateTimeForApi(dateTimeValue);
    const payload: ApplyBulkPodPayload = {
      ids: this.hawbIds,
      requestType: 'Shipment',
      dateTime,
      timeZoneId,
      driverName: (this.form.get('driverName')?.value ?? '').trim(),
      carrierName: (this.form.get('carrierName')?.value ?? '').trim(),
    };

    this.isSubmitting = true;
    this.statusCodesService
      .applyBulkPod(payload)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (resp: any) => {
          if (resp.statusCode === StatusCodes.Success) {
            this.snackbarService.openCustomisedSnackBar(
              resp.message,
              ModalPopupMessages.success
            );
            this.dialogRef.close(true);
          }
        },
      });
  }

  public getErrorMessage(controlName: string): string {
    return getPodFieldErrorMessage(this.form, controlName, this.errorMessages);
  }

  public checkControlHasError(controlName: string): boolean {
    return checkPodControlHasError(this.form, controlName, (form, name) =>
      !!this.commonService.checkControlError(form, name)
    );
  }

  public fetchWidth(event: MouseEvent, fieldName: string): void {
    this.commonService.setErrorTooltipData(
      event,
      fieldName,
      this.setTooltipInfoWidth,
      this.setInfoTooltipEvent,
      this.setInfoErrorTooltip
    );
  }

  private buildForm(): void {
    this.form = this.formBuilder.group(
      {
        driverName: ['', podDriverNameValidators(this.nameMaxLength)],
        carrierName: ['', podCarrierNameValidators(this.nameMaxLength)],
        dateTimeAndTimezone: [null, podOptionalDateTimeValidator()],
      },
      { validators: podCarrierRequiredValidator() }
    );

    this.applyControlState();
  }

  private applyControlState(): void {
    if (this.readOnly) {
      this.setReadOnlyState();
    } else if (this.podTimeOnly) {
      this.form.get('driverName')?.disable({ emitEvent: false });
      this.form.get('carrierName')?.disable({ emitEvent: false });
    }
  }

  private setReadOnlyState(): void {
    this.form.get('driverName')?.disable({ emitEvent: false });
    this.form.get('carrierName')?.disable({ emitEvent: false });
    this.form.get('dateTimeAndTimezone')?.disable({ emitEvent: false });
  }

  private getStoredTimeZoneId(): number {
    const storedTimeZoneId = +(
      this.localStorageService.getItem(STORAGE_KEYS.TIMEZONE_ID) ?? 0
    );
    return storedTimeZoneId > 0 ? storedTimeZoneId : 0;
  }

  private patchDetailsForm(): void {
    if (!this.isDetailsMode || !this.rowData) {
      return;
    }

    this.form.patchValue({
      driverName: this.rowData.driverName ?? '',
      carrierName: this.rowData.carrierName ?? '',
    });

    // FR-BPM-UI-005 / 020: the Manifest POD time defaults to now in the user's zone.
    const dateTime =
      this.rowData.dateTime ||
      (this.isManifest ? new Date().toISOString() : undefined);
    if (dateTime) {
      this.pendingDateTimePatch = {
        dateTime,
        timeZoneId:
          this.rowData.timeZoneId && +this.rowData.timeZoneId > 0
            ? +this.rowData.timeZoneId
            : this.defaultTimeZoneId,
      };
      this.applyPendingDateTimePatch();
    }
  }

  private applyPendingDateTimePatch(): void {
    if (!this.pendingDateTimePatch?.dateTime || !this.timeZones.length) {
      return;
    }

    const timeZoneId =
      this.pendingDateTimePatch.timeZoneId > 0
        ? this.pendingDateTimePatch.timeZoneId
        : this.timeZones.find(zone => zone.id === this.defaultTimeZoneId)
            ?.id ?? this.timeZones[0]?.id;

    if (!timeZoneId) {
      return;
    }

    this.form.patchValue({
      dateTimeAndTimezone: {
        dateTime: this.pendingDateTimePatch.dateTime,
        timeZoneId,
      },
    });
    this.form.get('dateTimeAndTimezone')?.updateValueAndValidity();
    if (this.readOnly) {
      this.setReadOnlyState();
    }
  }

  private buildDetailsResult(): BulkPodDetailsResult {
    const dateTimeValue = this.form.get('dateTimeAndTimezone')?.value;
    const hasPickerValue = !!dateTimeValue?.dateTime;

    if (!hasPickerValue) {
      return {
        driverName: (this.form.get('driverName')?.value ?? '').trim(),
        carrierName: (this.form.get('carrierName')?.value ?? '').trim(),
        dateTimeAndTimezone: null,
      };
    }

    const { dateTime, timeZoneId } = resolvePodDateTimeForApi(dateTimeValue);
    return {
      driverName: (this.form.get('driverName')?.value ?? '').trim(),
      carrierName: (this.form.get('carrierName')?.value ?? '').trim(),
      dateTimeAndTimezone: { dateTime, timeZoneId },
    };
  }

  private loadTimeZones(): void {
    this.commonService
      .getMasterLookUp(MasterLookup.timeZone)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: { data?: IDropdownResponse[] }) => {
          this.timeZones = resp.data ?? [];
          this.applyPendingDateTimePatch();
          this.form.get('dateTimeAndTimezone')?.updateValueAndValidity();
        },
      });
  }
}
